import { Icon } from '@chakra-ui/react';
import {
  ProductQuantityDisplayTypeEnum,
  ProductResWithMandatory,
  ProductTypeEnum,
  ProrationPolicyEnum,
} from '@monetize/types/app';
import { Identifier } from 'dnd-core';
import { FC, useRef } from 'react';
import { DragSourceMonitor, useDrag, useDrop } from 'react-dnd';
import { MdClose, MdDragIndicator, MdSettings } from 'react-icons/md';
import {
  MBox,
  MCheckbox,
  MCustomIconButton,
  MFlex,
  MGrid,
  MListItem,
  MText,
  MTooltip,
  MUnorderedList,
} from '../../../../components/Monetize';
import { ProductLookupPopover } from '../../../../components/Monetize/LookupPopover';
import {
  PRODUCT_STATUS_ENUM_DISPLAY,
  PRODUCT_TYPE_DISPLAY_SHORT,
  PRORATION_POLICY_DISPLAY,
} from '../../../../constants/products';
import BodyCell from './BodyCell';

type DragItem = {
  index: number;
  id: string;
  type: string;
};

const ItemTypes = {
  PRODUCT: 'PRODUCT',
};

interface DraggableProductRowProps {
  product: ProductResWithMandatory;
  index: number;
  totalColumns: string;
  isEditable: boolean;
  isReadOnly: boolean;
  isDisabled: boolean;
  showDragHandle: boolean;
  showAdvancedSettings: boolean;
  showPriceTierLock: boolean;
  allowQuantityDropdown: boolean;
  moveProduct: (dragIndex: number, hoverIndex: number) => void;
  saveProductReorder: () => void;
  onRemove: (product: ProductResWithMandatory, i: number) => void;
  onMandatoryCheckboxChange: (product?: ProductResWithMandatory) => void;
  onOpenAdvancedSettings: () => void;
  getRowColor: (index: number) => string;
  hasProductWithAdvancedSettings: boolean;
}

const DraggableProductRow: FC<DraggableProductRowProps> = ({
  product,
  index,
  totalColumns,
  isEditable,
  isReadOnly,
  isDisabled,
  showDragHandle,
  showAdvancedSettings,
  showPriceTierLock,
  allowQuantityDropdown,
  moveProduct,
  saveProductReorder,
  onRemove,
  onMandatoryCheckboxChange,
  onOpenAdvancedSettings,
  getRowColor,
  hasProductWithAdvancedSettings,
}) => {
  const dropContainerRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);

  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept: ItemTypes.PRODUCT,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!dropContainerRef.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect =
        dropContainerRef.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) {
        return;
      }

      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveProduct(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, dragPreview] = useDrag({
    type: ItemTypes.PRODUCT,
    item: { id: product.id, index, originalIndex: index },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item) => {
      if (item && item.originalIndex !== item.index) {
        saveProductReorder();
      }
    },
  });

  drag(dragHandleRef);
  drop(dragPreview(dropContainerRef));

  const bgColor = getRowColor(index);
  const opacity = isDragging ? 0.3 : 1;

  const advancedSettingsItems = [
    allowQuantityDropdown &&
    product.quantityDisplayType === ProductQuantityDisplayTypeEnum.DROPDOWN
      ? 'Show Quantity as Dropdown'
      : null,
    showPriceTierLock && product.isPriceTierLockEligible
      ? 'Price Tier Lock Eligible'
      : null,
  ].filter(Boolean) as string[];

  const advancedSettingsTooltip =
    advancedSettingsItems.length > 0 ? (
      <MBox>
        <MText color="tWhite.base" fontWeight="semibold" mb={1}>
          Configured Options
        </MText>
        <MUnorderedList pl={4}>
          {advancedSettingsItems.map((item) => (
            <MListItem fontSize="sm" fontWeight="normal" key={item}>
              {item}
            </MListItem>
          ))}
        </MUnorderedList>
      </MBox>
    ) : null;

  return (
    <MGrid
      ref={dropContainerRef}
      style={{ opacity }}
      gridTemplateColumns={totalColumns}
      data-handler-id={handlerId}
      alignItems="center"
      w="full"
    >
      <BodyCell bgColor={bgColor}>
        {showDragHandle && (
          <MFlex
            ref={dragHandleRef}
            justifyContent="center"
            alignItems="center"
          >
            <Icon boxSize={5} color="tPurple.dark" cursor="move" asChild>
              <MdDragIndicator />
            </Icon>
          </MFlex>
        )}
      </BodyCell>

      <BodyCell bgColor={bgColor}>
        <ProductLookupPopover productId={product.id} label={product.name} />
      </BodyCell>

      <BodyCell bgColor={bgColor}>
        <MFlex flexDir="column">
          <MText color="tPurple.base" textTransform="capitalize">
            {PRODUCT_TYPE_DISPLAY_SHORT[product.productType]}
          </MText>
          {product.productType === ProductTypeEnum.ADVANCE &&
            product.prorationPolicy === ProrationPolicyEnum.NON_PRORATED && (
              <MText>
                ({PRORATION_POLICY_DISPLAY[ProrationPolicyEnum.NON_PRORATED]})
              </MText>
            )}
        </MFlex>
      </BodyCell>

      <BodyCell bgColor={bgColor}>
        <MText color="tPurple.base" lineClamp={3} maxW="180px">
          {product.description}
        </MText>
      </BodyCell>

      <BodyCell bgColor={bgColor}>
        <MText color="tPurple.base" textTransform="capitalize">
          {PRODUCT_STATUS_ENUM_DISPLAY[product.status]}
        </MText>
      </BodyCell>

      <BodyCell bgColor={bgColor} justifyContent="center">
        <MCheckbox
          isDisabled={isDisabled}
          isChecked={product.isMandatory}
          onChange={() => onMandatoryCheckboxChange(product)}
        />
      </BodyCell>

      {showAdvancedSettings && advancedSettingsItems.length > 0 ? (
        <BodyCell bgColor={bgColor} justifyContent="center">
          <MTooltip label={advancedSettingsTooltip} placement="bottom">
            <MCustomIconButton
              iconColor="tRoyalBlue.base"
              aria-label="Configurations"
              variant="icon"
              icon={MdSettings}
              onClick={onOpenAdvancedSettings}
              cursor="pointer"
            />
          </MTooltip>
        </BodyCell>
      ) : (
        hasProductWithAdvancedSettings && (
          <BodyCell bgColor={bgColor} justifyContent="center">
            {/* Placeholder cell to keep the settings icon column aligned */}
          </BodyCell>
        )
      )}

      <BodyCell bgColor={bgColor} justify="right">
        {!isReadOnly && isEditable && (
          <MTooltip label="Remove" placement="bottom-end">
            <MCustomIconButton
              mr={2}
              title="Remove"
              aria-label="Remove"
              variant="icon"
              icon={MdClose}
              onClick={() => onRemove(product, index)}
              cursor="pointer"
            />
          </MTooltip>
        )}
      </BodyCell>
    </MGrid>
  );
};

export default DraggableProductRow;
